import { useMemo } from "react";
import * as XLSX from "xlsx";
import { fmtMoney } from "../parsers/xmlParser";
import { computeHonKitzba } from "../utils/honKitzba";
import { buildFundLedger, aggregateByEmployer } from "../utils/moneyComponentLedger";
import { computePitzuyimRows, computeTagmulimRows, getFundClass, computeHonBreakdown } from "../utils/reportComputations";
import { openReport } from "../utils/pdfEngine";

// ── helpers ───────────────────────────────────────────────────────────────────
const RETZEF = { "1": "כן", "2": "לא", "3": "אין מידע" };
// SUG_HAFRASHA codes per XSD standard 008:
// 1=פיצויים, 2=תגמולים עובד, 3=תגמולים מעביד, 4=תגמולים47, 10=תגמולים פרט

// getFundClass is now imported from reportComputations — kept inline for legacy compatibility
function _getFundClass(f) {
  const CAPITAL_CATS = ["קרן השתלמות", "קופת גמל להשקעה", "חיסכון לכל ילד"];
  if (CAPITAL_CATS.includes(f.cat) || f.sugMutzar === "9") return "הון";
  
  const sugKupa = f.retirementProjections?.[0]?.kupot?.[0]?.sugKupa || "";
  if (sugKupa.includes("לא משלמת")) return "קצבה לא משלמת";
  if (sugKupa.includes("משלמת")) return "קצבה משלמת";

  if (f.cat === "קרן פנסיה" || f.cat === "ביטוח מנהלים") return "קצבה משלמת";
  if (f.cat === "קופת גמל") return "קצבה לא משלמת";
  return "הון";
}

function fmt(v) {
  if (v === null || v === undefined) return "—";
  const n = parseFloat(v);
  if (isNaN(n) || n === 0) return "—";
  return n.toLocaleString("he-IL", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " ₪";
}

function exportXLSX(headers, rows, filename) {
  const data = rows.map(r => {
    const obj = {};
    headers.forEach((h, i) => { obj[h] = r[i] ?? ""; });
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(data);
  ws["!cols"] = headers.map(h => ({ wch: Math.max(h.length * 2, 14) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "\u05D3\u05D5\u05D7");
  XLSX.writeFile(wb, filename);
}

// \u2500\u2500 PDF print \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
// Builds a standalone printable HTML page (matching the modal's look) and opens
// it via the shared print engine \u2014 same "save as PDF" flow used in \u05EA\u05DE\u05D5\u05E0\u05EA \u05DC\u05E7\u05D5\u05D7.
function esc(v) {
  return String(v ?? "").replace(/[&<>]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
}

function buildTableHtml(headers, rows, highlightCol, sumCols) {
  const thead = headers.map((h, i) =>
    `<th style="padding:6px 8px;text-align:right;font-weight:700;font-size:11px;color:#475569;border-bottom:2px solid #e2e8f0;background:${i === highlightCol ? "#eff6ff" : "#f8fafc"}">${esc(h)}</th>`
  ).join("");

  const tbody = rows.map((row, ri) =>
    `<tr style="background:${ri % 2 === 0 ? "white" : "#fafafa"}">${row.map((cell, ci) => {
      const isNum = typeof cell === "number" || (typeof cell === "string" && cell.includes("\u20AA"));
      const color = cell === "\u2014" ? "#cbd5e1" : ci === highlightCol ? "#1d4ed8" : "#1e293b";
      return `<td style="padding:5px 8px;border-bottom:1px solid #f1f5f9;text-align:${isNum ? "left" : "right"};font-weight:${ci === 0 ? 600 : 400};color:${color}">${esc(cell)}</td>`;
    }).join("")}</tr>`
  ).join("");

  let totals = "";
  if (rows.length > 1) {
    const totalsRow = headers.map((h, ci) => {
      if (ci === 0) return "\u05E1\u05D4\u05F4\u05DB";
      if (ci === 1) return "";
      if (sumCols && !sumCols.includes(ci)) return "";
      const sum = rows.reduce((a, r) => {
        const v = typeof r[ci] === "string" ? parseFloat(r[ci].replace(/[^\d.]/g, "")) : r[ci];
        return a + (isNaN(v) ? 0 : v);
      }, 0);
      return sum > 0 ? sum.toLocaleString("he-IL", { maximumFractionDigits: 0 }) + " \u20AA" : "";
    });
    totals = `<tr style="background:#f0f9ff;font-weight:700;border-top:2px solid #bfdbfe">${totalsRow.map((cell, ci) =>
      `<td style="padding:8px 10px;color:#1d4ed8;font-size:12px;text-align:${ci === 0 ? "right" : "left"}">${esc(cell)}</td>`
    ).join("")}</tr>`;
  }

  return `<table style="width:100%;border-collapse:collapse;font-size:11px;direction:rtl"><thead><tr>${thead}</tr></thead><tbody>${tbody}${totals}</tbody></table>`;
}

function printReport({ title, icon, color, clientName, headers, rows, summary = [], highlightCol, sumCols }) {
  const dateStr = new Date().toLocaleDateString("he-IL");
  const summaryHtml = summary.length
    ? `<div style="display:flex;flex-wrap:wrap;gap:12px;margin-bottom:20px">${summary.map(s =>
        `<div style="flex:1;min-width:130px;background:${s.bg};border-radius:10px;padding:10px 14px;border:1px solid ${s.color}30">
           <div style="font-size:11px;color:#64748b">${esc(s.label)}</div>
           <div style="font-size:16px;font-weight:800;color:${s.color};margin-top:2px">${esc(s.value)}</div>
         </div>`).join("")}</div>`
    : "";

  const html = `<!DOCTYPE html><html lang="he" dir="rtl"><head>
      <meta charset="utf-8" />
      <title>${esc(title)}${clientName ? ` \u2014 ${esc(clientName)}` : ""}</title>
      <link href="https://fonts.googleapis.com/css2?family=Heebo:wght@400;600;700;800&display=swap" rel="stylesheet" />
      <style>
        body, body * { font-family: 'Heebo', 'Segoe UI', Arial, sans-serif !important; }
        @page { size: A4 landscape; margin: 12mm; }
        @media print { .no-print { display: none !important; } }
      </style>
    </head>
    <body style="margin:0;padding:24px;background:white;color:#1e293b">
      <div style="max-width:1100px;margin:0 auto">
        <div class="no-print" style="text-align:left;margin-bottom:16px">
          <button onclick="window.print()" style="background:${color};color:white;border:none;padding:10px 20px;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer">\uD83D\uDDA8\uFE0F \u05D4\u05D3\u05E4\u05E1 / \u05E9\u05DE\u05D5\u05E8 PDF</button>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid ${color};padding-bottom:12px;margin-bottom:20px">
          <div>
            <div style="font-size:22px;font-weight:800;color:${color}">${esc(icon)} ${esc(title)}</div>
            ${clientName ? `<div style="font-size:13px;color:#64748b;margin-top:4px">\u05DC\u05E7\u05D5\u05D7: ${esc(clientName)}</div>` : ""}
          </div>
          <div style="font-size:11px;color:#94a3b8;text-align:left;line-height:1.6">\u05E0\u05DB\u05D5\u05DF \u05DC\u05EA\u05D0\u05E8\u05D9\u05DA \u05D4\u05D3\u05D9\u05D5\u05D5\u05D7 \u05D4\u05D0\u05D7\u05E8\u05D5\u05DF<br/>\u05D4\u05D5\u05E4\u05E7 \u05D1\u05EA\u05D0\u05E8\u05D9\u05DA ${dateStr}</div>
        </div>
        ${summaryHtml}
        ${buildTableHtml(headers, rows, highlightCol, sumCols)}
      </div>
    </body></html>`;

  openReport(html);
}

// ── Modal wrapper ─────────────────────────────────────────────────────────────
function Modal({ title, icon, color, onClose, children, onExport, onPrint, inline }) {
  return (
    <div style={{
      position: inline ? "absolute" : "fixed", inset: 0, 
      background: inline ? "rgba(30, 41, 59, 0.4)" : "rgba(0,0,0,0.5)", // Darker overlay for contrast
      backdropFilter: inline ? "blur(2px)" : "none",
      zIndex: inline ? 100 : 1000, display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: inline ? "12px" : "24px 16px", overflowY: "auto",
    }} onClick={onClose}>
      <div style={{
        background: "white", borderRadius: 16, width: "100%", maxWidth: inline ? "100%" : 1000,
        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.2)", 
        overflow: "hidden",
        marginTop: inline ? "10px" : "0",
        marginBottom: inline ? "10px" : "0",
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg,${color}dd,${color})`,
          padding: "16px 20px", color: "white",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{icon} {title}</div>
            <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>נכון לתאריך הדיווח האחרון</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {onPrint && (
              <button onClick={onPrint} style={{
                background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)",
                color: "white", padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600,
              }}>🖨️ הדפס PDF</button>
            )}
            {onExport && (
              <button onClick={onExport} style={{
                background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)",
                color: "white", padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600,
              }}>⬇️ ייצוא Excel</button>
            )}
            <button onClick={onClose} style={{
              background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)",
              color: "white", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 14,
            }}>✕</button>
          </div>
        </div>
        <div style={{ padding: 20, overflowX: "auto" }}>{children}</div>
      </div>
    </div>
  );
}

// ── Table component ───────────────────────────────────────────────────────────
function DataTable({ headers, rows, highlightCol, sumCols }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, direction: "rtl" }}>
      <thead>
        <tr style={{ background: "#f8fafc" }}>
          {headers.map((h, i) => (
            <th key={i} style={{
              padding: "6px 8px", textAlign: "right", fontWeight: 700, fontSize: 11,
              color: "#475569", borderBottom: "2px solid #e2e8f0",
              background: i === highlightCol ? "#eff6ff" : "#f8fafc",
            }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} style={{ background: ri % 2 === 0 ? "white" : "#fafafa" }}>
            {row.map((cell, ci) => (
              <td key={ci} style={{
                padding: "5px 8px", borderBottom: "1px solid #f1f5f9",
                textAlign: typeof cell === "number" || (typeof cell === "string" && cell.includes("₪")) ? "left" : "right",
                fontWeight: ci === 0 ? 600 : 400,
                color: cell === "—" ? "#cbd5e1" : ci === highlightCol ? "#1d4ed8" : "#1e293b",
                background: ci === highlightCol ? "#eff6ff80" : "transparent",
              }}>{cell}</td>
            ))}
          </tr>
        ))}
        {/* Totals row */}
        {rows.length > 1 && (() => {
          const totalsRow = headers.map((h, ci) => {
            if (ci === 0) return "סה״כ";
            if (ci === 1) return "";
            if (sumCols && !sumCols.includes(ci)) return "";
            // Sum money columns
            const vals = rows.map(r => {
              const v = typeof r[ci] === "string" ? parseFloat(r[ci].replace(/[^\d.]/g, "")) : r[ci];
              return isNaN(v) ? 0 : v;
            });
            const total = vals.reduce((a, b) => a + b, 0);
            return total > 0 ? total.toLocaleString("he-IL", { maximumFractionDigits: 0 }) + " ₪" : "";
          });
          return (
            <tr style={{ background: "#f0f9ff", fontWeight: 700, borderTop: "2px solid #bfdbfe" }}>
              {totalsRow.map((cell, ci) => (
                <td key={ci} style={{
                  padding: "8px 10px", color: "#1d4ed8", fontSize: 12,
                  textAlign: ci === 0 ? "right" : "left",
                }}>{cell}</td>
              ))}
            </tr>
          );
        })()}
      </tbody>
    </table>
  );
}

// ── PITZUYIM MODAL ────────────────────────────────────────────────────────────
export function PitzuyimModal({ funds, onClose, inline, clientName }) {
  const rows = useMemo(() => computePitzuyimRows(funds), [funds]);

  const headers = [
    "שם יצרן", "מספר תוכנית", "שם מעסיק", "הון", "קצבה משלמת", "קצבה לא משלמת",
    "פיצויים פטורים מעבידים קודמים",
    "ערך פדיון לקצבה מעבידים קודמים",
    "פיצויים ברצף קצבה",
    "פיצויים ברצף זכויות",
    "פיצויים 31.12.1999",
    "ערך פדיון מעסיק נוכחי",
    "מרכיב פיצויים למס",
    "פיצויים נזילים להון",
    "יתרה ללא התחשבנות",
    "רצף קצבה",
    "רצף זכויות",
  ];

  const tableRows = rows.map(r => [
    r.company, r.polNum, r.employerName,
    fmt(r.honAmount), fmt(r.kitzbaMeshAmount), fmt(r.kitzbaLoMeshAmount),
    fmt(r.pturimMaavidimKodmim),
    fmt(r.lekitzba),
    fmt(r.beretzefKitzba),
    fmt(r.beretzefZechuyot),
    fmt(r.pitzuyim1999),
    fmt(r.erkeiMaasikNochechi),
    fmt(r.markivLemas),
    fmt(r.lehon),
    fmt(r.llotHitchashbenot),
    r.retzefPitzuyimKitzba,
    r.retzefZechuyot === "כן" ? <span style={{ color: "#dc2626", fontWeight: 700 }}>{r.retzefZechuyot}</span> : r.retzefZechuyot,
  ]);

  const totalPitzuyim = rows.reduce((s, r) => s + r.erkeiMaasikNochechi, 0);

  const summary = [
    { label: "סה״כ פיצויים", value: fmt(totalPitzuyim), color: "#d97706", bg: "#fffbeb" },
    { label: "מוצרים עם פיצויים", value: rows.length, color: "#475569", bg: "#f8fafc" },
    { label: "ממעסיק נוכחי", value: fmt(rows.reduce((s,r) => s+r.erkeiMaasikNochechi, 0)), color: "#0d9488", bg: "#f0fdfa" },
    { label: "ללא התחשבנות", value: fmt(rows.reduce((s,r) => s+r.llotHitchashbenot, 0)), color: "#7c3aed", bg: "#faf5ff" },
  ];

  return (
    <Modal title="ריכוז יתרות פיצויים" icon="💼" color="#d97706" onClose={onClose} inline={inline}
      onExport={() => exportXLSX(headers, tableRows, "pitzuyim.xlsx")}
      onPrint={() => printReport({ title: "ריכוז יתרות פיצויים", icon: "💼", color: "#d97706", clientName, headers, rows: tableRows, summary, highlightCol: 8 })}>

      {/* Summary strip */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        {summary.map((item, i) => (
          <div key={i} style={{ flex: 1, background: item.bg, borderRadius: 10, padding: "10px 14px", border: `1px solid ${item.color}30` }}>
            <div style={{ fontSize: 10, color: "#64748b" }}>{item.label}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: item.color, marginTop: 2 }}>{item.value}</div>
          </div>
        ))}
      </div>

      {rows.length > 0 ? (
        <DataTable headers={headers} rows={tableRows} highlightCol={8} />
      ) : (
        <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>לא נמצאו נתוני פיצויים</div>
      )}
    </Modal>
  );
}

// ── TAGMULIM MODAL ────────────────────────────────────────────────────────────
export function TagmulimModal({ funds, onClose, inline, clientName }) {
  const rows = useMemo(() => computeTagmulimRows(funds), [funds]);

  const headers = [
    "שם יצרן", "מספר תוכנית", "סוג תוכנית", "סוג קופה",
    "סה״כ ערך פדיון", "תגמולים", "כספים אישיים",
    "הון", "קצבה משלמת", "קצבה לא משלמת",
    "קצבה חודשית", "תשואה",
  ];

  const tableRows = rows.map(r => [
    r.company, r.polNum, r.tochnit, r.kupaType,
    fmt(r.totalRedeem),
    fmt(r.tagmulim),
    fmt(r.ishi),
    fmt(r.hon),
    fmt(r.kitzbaMesh),
    fmt(r.kitzbaLoMesh),
    r.pension > 0 ? fmt(r.pension) + "/חודש" : "—",
    r.tsua > 0 ? r.tsua.toFixed(2) + "%" : "—",
  ]);

  const totals = {
    redeem:       rows.reduce((s, r) => s + r.totalRedeem, 0),
    tagmulim:     rows.reduce((s, r) => s + r.tagmulim, 0),
    pitzuyim:     rows.reduce((s, r) => s + r.pitzuyim, 0),
    ishi:         rows.reduce((s, r) => s + r.ishi, 0),
    hon:          rows.reduce((s, r) => s + r.hon, 0),
    kitzbaMesh:   rows.reduce((s, r) => s + r.kitzbaMesh, 0),
    kitzbaLoMesh: rows.reduce((s, r) => s + r.kitzbaLoMesh, 0),
    pension:      rows.reduce((s, r) => s + r.pension, 0),
  };

  const summary = [
    { label: "סה״כ ערך פדיון", value: fmt(totals.redeem), color: "#2563eb", bg: "#eff6ff" },
    { label: "תגמולים", value: fmt(totals.tagmulim), color: "#0d9488", bg: "#f0fdfa" },
    { label: "קצבה משלמת", value: fmt(totals.kitzbaMesh), color: "#7c3aed", bg: "#faf5ff" },
    { label: "קצבה חודשית", value: fmt(totals.pension) + "/חודש", color: "#16a34a", bg: "#f0fdf4" },
  ];

  return (
    <Modal title="ריכוז תגמולים" icon="📊" color="#2563eb" onClose={onClose} inline={inline}
      onExport={() => exportXLSX(headers, tableRows, "tagmulim.xlsx")}
      onPrint={() => printReport({ title: "ריכוז תגמולים", icon: "📊", color: "#2563eb", clientName, headers, rows: tableRows, summary, highlightCol: 4 })}>

      {/* Summary strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10, marginBottom: 20 }}>
        {summary.map((item, i) => (
          <div key={i} style={{ background: item.bg, borderRadius: 10, padding: "10px 12px", border: `1px solid ${item.color}30` }}>
            <div style={{ fontSize: 10, color: "#64748b" }}>{item.label}</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: item.color, marginTop: 2, fontFamily: "'Inter', sans-serif" }}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* Chart - visual breakdown */}
      <div style={{ display: "flex", gap: 4, height: 8, borderRadius: 4, overflow: "hidden", marginBottom: 16 }}>
        {[
          { val: totals.tagmulim, color: "#0d9488" },
          { val: totals.ishi, color: "#6366f1" },
        ].filter(x => x.val > 0).map((seg, i) => (
          <div key={i} style={{
            flex: seg.val, background: seg.color,
          }} />
        ))}
      </div>
      <div style={{ display: "flex", gap: 16, marginBottom: 16, fontSize: 11, color: "#64748b" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: "#0d9488", display: "inline-block" }}/> תגמולים</span>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: "#6366f1", display: "inline-block" }}/> כספים אישיים</span>
      </div>

      <DataTable headers={headers} rows={tableRows} highlightCol={4} />
    </Modal>
  );
}

// ── HON MODAL ─────────────────────────────────────────────────────────────────
export function HonModal({ funds, onClose, inline, clientName }) {
  const rows = useMemo(() => {
    return computeHonBreakdown(funds).rows
      .map(r => ({ ...r, total: r.tagmulimHon + r.hashtalamut + r.pitzuyimHon + (r.kitzbaUntil2000 || 0) }))
      .filter(r => r.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [funds]);

  const headers = [
    "שם יצרן", "מספר תוכנית", "סוג תוכנית", "סוג קופה",
    "סה״כ בהון", "תגמולים בהון", "השתלמות", "תיקון 190", "פיצויים בהון", "קצבה (עד 2000)"
  ];

  const tableRows = rows.map(({ f, total, tagmulimHon, hashtalamut, tikun190, pitzuyimHon, kitzbaUntil2000 }) => [
    f.companyName,
    f.polNum,
    f.tochnit,
    f.cat,
    fmt(total),
    fmt(tagmulimHon),
    fmt(hashtalamut),
    tikun190 > 0 ? fmt(tikun190) : "—",
    fmt(pitzuyimHon),
    (kitzbaUntil2000 || 0) > 0 ? fmt(kitzbaUntil2000) : "—",
  ]);

  const totals = rows.reduce((acc, r) => ({
    total:       acc.total       + r.total,
    tagmulimHon: acc.tagmulimHon + r.tagmulimHon,
    hashtalamut: acc.hashtalamut + r.hashtalamut,
    tikun190:    acc.tikun190    + r.tikun190,
    pitzuyimHon: acc.pitzuyimHon + r.pitzuyimHon,
    kitzbaUntil2000: acc.kitzbaUntil2000 + (r.kitzbaUntil2000 || 0),
  }), { total: 0, tagmulimHon: 0, hashtalamut: 0, tikun190: 0, pitzuyimHon: 0, kitzbaUntil2000: 0 });

  const summary = [
    { label: "סה״כ בהון",      value: fmt(totals.total),       color: "#2563eb", bg: "#eff6ff" },
    { label: "תגמולים בהון",   value: fmt(totals.tagmulimHon), color: "#0d9488", bg: "#f0fdfa" },
    { label: "השתלמות",        value: fmt(totals.hashtalamut), color: "#7c3aed", bg: "#faf5ff" },
    { label: "פיצויים בהון",   value: fmt(totals.pitzuyimHon), color: "#d97706", bg: "#fffbeb" },
    { label: "קצבה (עד 2000)", value: fmt(totals.kitzbaUntil2000), color: "#0f766e", bg: "#ccfbf1" },
  ];

  return (
    <Modal title="ריכוז כספים בהון" icon="💰" color="#2563eb" onClose={onClose} inline={inline}
      onExport={() => exportXLSX(headers, tableRows, "hon.xlsx")}
      onPrint={() => printReport({ title: "ריכוז כספים בהון", icon: "💰", color: "#2563eb", clientName, headers, rows: tableRows, summary, highlightCol: 4 })}>

      {/* Summary strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginBottom: 20 }}>
        {summary.map((item, i) => (
          <div key={i} style={{ background: item.bg, borderRadius: 10, padding: "10px 12px", border: `1px solid ${item.color}30` }}>
            <div style={{ fontSize: 10, color: "#64748b" }}>{item.label}</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: item.color, marginTop: 2 }}>{item.value}</div>
          </div>
        ))}
      </div>

      <DataTable headers={headers} rows={tableRows} highlightCol={4} />
    </Modal>
  );
}

// ── KITZBA MODAL ──────────────────────────────────────────────────────────────
export function KitzbaModal({ funds, onClose, inline, clientName }) {
  const rows = useMemo(() => {
    return funds
      .filter(f => f.cat !== "קרן השתלמות")
      .map(f => {
        const hk = computeHonKitzba(f);
        const tagmulimKitzba  = (hk.kitzba || 0) - (hk.kitzbaPreSettlement || 0);
        const pitzuyimKitzba  = hk.kitzbaPitzuyim || 0;
        const total           = tagmulimKitzba + pitzuyimKitzba;
        const fundClass       = getFundClass(f);
        const kitzbaMeshelet  = fundClass === "קצבה משלמת"    ? total : 0;
        const kitzbaLoMesh    = fundClass === "קצבה לא משלמת" ? total : 0;
        const pensionMonthly  = f.retirementProjections?.[0]?.kupot?.[0]?.pensionMonthly
                             || f.retirementProjections?.[0]?.kupot?.[0]?.pension || 0;
        return { f, total, tagmulimKitzba, pitzuyimKitzba, kitzbaMeshelet, kitzbaLoMesh, pensionMonthly };
      })
      .filter(r => r.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [funds]);

  const headers = [
    "שם יצרן", "מספר תוכנית", "סוג תוכנית", "סוג קופה",
    "סה״כ בקצבה", "תגמולים בקצבה", "פיצויים בקצבה",
    "קצבה משלמת", "קצבה לא משלמת", "קצבה חודשית",
  ];

  const tableRows = rows.map(({ f, total, tagmulimKitzba, pitzuyimKitzba, kitzbaMeshelet, kitzbaLoMesh, pensionMonthly }) => [
    f.companyName, f.polNum, f.tochnit, f.cat,
    fmt(total),
    fmt(tagmulimKitzba),
    pitzuyimKitzba > 0 ? fmt(pitzuyimKitzba) : "—",
    kitzbaMeshelet > 0 ? fmt(kitzbaMeshelet) : "—",
    kitzbaLoMesh   > 0 ? fmt(kitzbaLoMesh)   : "—",
    pensionMonthly > 0 ? `${fmt(pensionMonthly)} ₪/חודש` : "—",
  ]);

  const totals = rows.reduce((acc, r) => ({
    total:          acc.total          + r.total,
    tagmulimKitzba: acc.tagmulimKitzba + r.tagmulimKitzba,
    pitzuyimKitzba: acc.pitzuyimKitzba + r.pitzuyimKitzba,
    pensionMonthly: acc.pensionMonthly + r.pensionMonthly,
  }), { total: 0, tagmulimKitzba: 0, pitzuyimKitzba: 0, pensionMonthly: 0 });

  const summary = [
    { label: "סה״כ בקצבה",    value: fmt(totals.total),          color: "#16a34a", bg: "#f0fdf4" },
    { label: "תגמולים בקצבה", value: fmt(totals.tagmulimKitzba), color: "#0d9488", bg: "#f0fdfa" },
    { label: "פיצויים בקצבה", value: fmt(totals.pitzuyimKitzba), color: "#d97706", bg: "#fffbeb" },
    { label: "קצבה חודשית",   value: totals.pensionMonthly > 0 ? `${fmt(totals.pensionMonthly)} ₪/חודש` : "—", color: "#7c3aed", bg: "#faf5ff" },
  ];

  return (
    <Modal title="ריכוז כספים בקצבה" icon="📅" color="#16a34a" onClose={onClose} inline={inline}
      onExport={() => exportXLSX(headers, tableRows, "kitzba.xlsx")}
      onPrint={() => printReport({ title: "ריכוז כספים בקצבה", icon: "📅", color: "#16a34a", clientName, headers, rows: tableRows, summary, highlightCol: 4 })}>

      <div className="responsive-four-col" style={{ gap: 10, marginBottom: 20 }}>
        {summary.map((item, i) => (
          <div key={i} style={{ background: item.bg, borderRadius: 10, padding: "10px 12px", border: `1px solid ${item.color}30` }}>
            <div style={{ fontSize: 10, color: "#64748b" }}>{item.label}</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: item.color, marginTop: 2 }}>{item.value}</div>
          </div>
        ))}
      </div>

      <DataTable headers={headers} rows={tableRows} highlightCol={4} />
    </Modal>
  );
}

// ── MONEY COLOR MODAL ─────────────────────────────────────────────────────────
// "צבע כסף" — פירוק כל כספי הלקוח לפי (מעסיק × סוג רכיב), על פני כל הקרנות.
// ניזון מאותו moneyComponentLedger שמזין את computeHonBucketsFromFunds
// (הגשר לתחזית) — כך שהתצוגה כאן תמיד תואמת למה שנמשך בטאב תחזית.
const COLOR_ORDER = [
  "תגמולים הון", "תגמולים קצבה", "תיקון 190", "פיצויים הון", "פיצויים קצבה",
  "נתון לבחירה", "נתון לבחירה (פיצויים)", 'קה"ש', "הון נזיל",
];

export function MoneyColorModal({ funds, onClose, inline, clientName }) {
  const byEmployer = useMemo(() => {
    const rows = (funds || []).flatMap(buildFundLedger);
    return aggregateByEmployer(rows);
  }, [funds]);

  const presentColors = useMemo(
    () => COLOR_ORDER.filter(c => byEmployer.some(e => (e.byColor.get(c) || 0) > 0)),
    [byEmployer]
  );

  const headers = ["מעסיק", ...presentColors, "סה״כ"];

  // כשכמה כתיבים של אותו מעסיק אמיתי מוזגו (רווחים/פיסוק שונים במסלקה),
  // מציגים את שאר הווריאציות בסוגריים — כדי שהאיחוד יהיה שקוף וניתן לבדיקה,
  // לא מוסתר.
  const employerCell = e => {
    const others = e.rawNames.filter(n => n !== e.employer);
    return others.length ? `${e.employer} (מוזג: ${others.join(", ")})` : e.employer;
  };

  const tableRows = byEmployer.map(e => [
    employerCell(e),
    ...presentColors.map(c => { const v = e.byColor.get(c) || 0; return v > 0 ? fmt(v) : "—"; }),
    fmt(e.total),
  ]);

  const grandTotal = byEmployer.reduce((s, e) => s + e.total, 0);
  const summary = presentColors.map((c, i) => ({
    label: c,
    value: fmt(byEmployer.reduce((s, e) => s + (e.byColor.get(c) || 0), 0)),
    color: ["#0d9488", "#16a34a", "#9333ea", "#d97706", "#0f766e", "#2563eb", "#c2410c", "#0891b2", "#64748b"][i % 9],
    bg: "#f8fafc",
  }));

  return (
    <Modal title="צבע כסף — פירוק לפי מעסיק וסוג רכיב" icon="🎨" color="#7c3aed" onClose={onClose} inline={inline}
      onExport={() => exportXLSX(headers, tableRows, "money-color.xlsx")}
      onPrint={() => printReport({ title: "צבע כסף — פירוק לפי מעסיק וסוג רכיב", icon: "🎨", color: "#7c3aed", clientName, headers, rows: tableRows, summary, highlightCol: headers.length - 1 })}>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginBottom: 20 }}>
        {summary.map((item, i) => (
          <div key={i} style={{ background: item.bg, borderRadius: 10, padding: "10px 12px", border: `1px solid ${item.color}30` }}>
            <div style={{ fontSize: 10, color: "#64748b" }}>{item.label}</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: item.color, marginTop: 2 }}>{item.value}</div>
          </div>
        ))}
        <div style={{ background: "#f5f3ff", borderRadius: 10, padding: "10px 12px", border: "1px solid #7c3aed30" }}>
          <div style={{ fontSize: 10, color: "#64748b" }}>סה״כ</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#7c3aed", marginTop: 2 }}>{fmt(grandTotal)}</div>
        </div>
      </div>

      <DataTable headers={headers} rows={tableRows} highlightCol={headers.length - 1} />
    </Modal>
  );
}

// ── KISUIM MODAL ──────────────────────────────────────────────────────────────
// ריכוז כיסויים ביטוחיים — שורה לכל כיסוי (פוליסה × כיסוי), כולל מטריות קולקטיביות.

// סכום הכיסוי: סכום פר-כיסוי (SCHUM-BITUACH) ואם אין — סכום המוות מהבלוק היסודי
const kisuiSchum = k => k.schumBituach || k.bituachLemavet || null;
const isDeathKisui = k => !!k.bituachLemavet || (k.sugKisuy || "").includes("מוות") || (k.sugKisuy || "").includes("משולבת");
const isOkaKisui   = k => (k.sugKisuy || "").includes('אכ"ע') || (k.sugKisuy || "").includes("אבדן כושר") || (k.sugKisuy || "").includes("שחרור");

// מוצרי חיסכון טהור — לא רלוונטיים לריכוז כיסויים ביטוחיים
const KISUIM_EXCLUDED_CATS = ["קרן השתלמות", "קופת גמל", "קופת גמל להשקעה", "חיסכון לכל ילד"];

export function computeKisuimRows(funds) {
  const rows = [];
  for (const f of funds || []) {
    if (KISUIM_EXCLUDED_CATS.includes(f.cat)) continue;
    for (const k of f.kisuim || []) {
      const schum = kisuiSchum(k);
      const okaMonthly = isOkaKisui(k) ? (k.schumSheur || k.schumBituach || null) : null;
      rows.push({
        f, k,
        company: f.companyName, polNum: f.polNum, cat: f.cat,
        statusLabel: f.statusLabel,
        kisuiName: k.sugKisuy || k.name || "כיסוי",
        yatzranName: k.name,
        sugMevutach: k.sugMevutach,
        schum,
        deathSum: isDeathKisui(k) ? (k.bituachLemavet || schum || 0) : 0,
        okaMonthly,
        achuzMeSachar: k.achuzMeSachar,
        premium: k.premium,
        alutKisui: k.alutKisui,
        startDate: k.startDate, endDate: k.endDate,
        isMitriya: false,
      });
    }
    for (const m of f.mitriyot || []) {
      rows.push({
        f, k: null,
        company: f.companyName, polNum: f.polNum, cat: f.cat,
        statusLabel: f.statusLabel,
        kisuiName: `${m.sugMutzar || "כיסוי"} (מטרייה)`,
        yatzranName: m.mevatachat ? `מבטחת: ${m.mevatachat}` : null,
        sugMevutach: null,
        schum: m.schum,
        deathSum: (m.sugMutzar || "").includes("מוות") ? (m.schum || 0) : 0,
        okaMonthly: (m.sugMutzar || "").includes("אבדן כושר") ? m.schum : null,
        achuzMeSachar: null,
        premium: null,
        alutKisui: m.alut,
        startDate: m.startDate, endDate: m.endDate,
        isMitriya: true,
      });
    }
  }
  return rows;
}

// סה"כ כיסוי מוות על פני כל המוצרים — משמש גם את כרטיס "כיסוי ביטוחי כולל" בדשבורד
export function computeTotalDeathCover(funds) {
  return computeKisuimRows(funds).reduce((s, r) => s + (r.deathSum || 0), 0);
}

export function KisuimModal({ funds, onClose, inline, clientName }) {
  const rows = useMemo(() => computeKisuimRows(funds), [funds]);

  const headers = [
    "שם יצרן", "מספר תוכנית", "סוג מוצר", "סוג כיסוי", "שם כיסוי אצל היצרן",
    "מבוטח", "סכום ביטוח", 'אכ"ע חודשי', "% מהשכר",
    "פרמיה", "עלות כיסוי", "תחילת כיסוי", "תום כיסוי", "סטטוס",
  ];

  const tableRows = rows.map(r => [
    r.company, r.polNum, r.cat,
    r.kisuiName,
    r.yatzranName || "—",
    r.sugMevutach || "—",
    fmt(r.schum),
    r.okaMonthly ? fmt(r.okaMonthly) + "/חודש" : "—",
    r.achuzMeSachar ? r.achuzMeSachar + "%" : "—",
    fmt(r.premium),
    fmt(r.alutKisui),
    r.startDate || "—",
    r.endDate || "—",
    r.statusLabel || "—",
  ]);

  const totals = {
    death:   rows.reduce((s, r) => s + (r.deathSum || 0), 0),
    oka:     rows.reduce((s, r) => s + (r.okaMonthly || 0), 0),
    premium: rows.reduce((s, r) => s + (r.premium || 0) + (r.alutKisui && !r.premium ? r.alutKisui : 0), 0),
  };

  const summary = [
    { label: "סה״כ כיסוי למקרה מוות", value: fmt(totals.death), color: "#e11d48", bg: "#fff1f2" },
    { label: 'אכ"ע חודשי מצטבר', value: totals.oka > 0 ? fmt(totals.oka) + "/חודש" : "—", color: "#0d9488", bg: "#f0fdfa" },
    { label: "סה״כ פרמיה / עלות", value: fmt(totals.premium), color: "#7c3aed", bg: "#faf5ff" },
    { label: "כיסויים", value: rows.length, color: "#475569", bg: "#f8fafc" },
  ];

  return (
    <Modal title="ריכוז כיסויים ביטוחיים" icon="🛡️" color="#e11d48" onClose={onClose} inline={inline}
      onExport={() => exportXLSX(headers, tableRows, "kisuim.xlsx")}
      onPrint={() => printReport({ title: "ריכוז כיסויים ביטוחיים", icon: "🛡️", color: "#e11d48", clientName, headers, rows: tableRows, summary, highlightCol: 6, sumCols: [6, 7, 9, 10] })}>

      {/* Summary strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginBottom: 20 }}>
        {summary.map((item, i) => (
          <div key={i} style={{ background: item.bg, borderRadius: 10, padding: "10px 12px", border: `1px solid ${item.color}30` }}>
            <div style={{ fontSize: 10, color: "#64748b" }}>{item.label}</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: item.color, marginTop: 2 }}>{item.value}</div>
          </div>
        ))}
      </div>

      {rows.length > 0 ? (
        <DataTable headers={headers} rows={tableRows} highlightCol={6} sumCols={[6, 7, 9, 10]} />
      ) : (
        <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>לא נמצאו כיסויים ביטוחיים</div>
      )}
    </Modal>
  );
}

// ── ALERTS MODAL ──────────────────────────────────────────────────────────────
export function AlertsModal({ funds, onClose, inline }) {
  const alertFunds = useMemo(() => {
    return funds.filter(f => f.debts || f.hasShiabud || f.loan);
  }, [funds]);

  const headers = ["חברה", "מוצר", "פוליסה / חשבון", "סוג התראה", "תיאור"];

  const tableRows = alertFunds.map(f => {
    const alerts = [];
    if (f.debts)      alerts.push({ type: "פיגור", color: "#dc2626", bg: "#fee2e2", icon: "⚠️" });
    if (f.hasShiabud) alerts.push({ type: "שעבוד / עיקול", color: "#d97706", bg: "#fef9c3", icon: "🔒" });
    if (f.loan)       alerts.push({ type: "הלוואה קיימת", color: "#9d174d", bg: "#fce7f3", icon: "💰" });

    return [
      f.companyName,
      f.cat,
      f.polNum || "—",
      <div style={{ display: "flex", gap: 4 }}>
        {alerts.map((a, i) => (
          <span key={i} style={{
            background: a.bg, color: a.color, padding: "2px 8px",
            borderRadius: 12, fontSize: 10, fontWeight: 700, border: `1px solid ${a.color}30`
          }}>{a.icon} {a.type}</span>
        ))}
      </div>,
      f.debts ? "פיגור בתשלומים שוטפים" : f.hasShiabud ? "קיימת הגבלה על הנזילות" : "קיימת יתרת הלוואה לפרעון"
    ];
  });

  return (
    <Modal title="ריכוז התראות וחריגות" icon="🔔" color="#dc2626" onClose={onClose} inline={inline}>
      <div style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
          להלן רשימת המוצרים בהם זוהו חריגות המחייבות תשומת לב (פיגורים, שעבודים או הלוואות).
        </p>
      </div>

      {alertFunds.length > 0 ? (
        <DataTable headers={headers} rows={tableRows} />
      ) : (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "#94a3b8" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>כיסוי מלא ותקין</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>לא נמצאו פיגורים, שעבודים או הלוואות פתוחות.</div>
        </div>
      )}
    </Modal>
  );
}
